import { inject } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '@app/core/services/auth.service';

export const roleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredRole = route.data['requiredRole'];
  // 2026-06-08: optional finer-grained permission check on top of the
  // role check, used to gate corporate sub-user features. See
  // AuthService.hasPermission() for the lookup logic.
  const requiredPermission = route.data['requiredPermission'] as string | undefined;

  // No requiredRole specified — guard is a passthrough (route is open to any
  // authenticated user; authGuard upstream handles the auth check).
  if (!requiredRole && !requiredPermission) {
    return true;
  }

  // Empty array — treat as no-op passthrough (configuration edge case).
  // Without this check, [].includes(x) === false would redirect every user.
  if (Array.isArray(requiredRole) && requiredRole.length === 0 && !requiredPermission) {
    return true;
  }

  const userRole = authService.getUserRole();

  // Not authenticated — defence-in-depth fallback. authGuard SHOULD have
  // caught this upstream, but if route ordering is misconfigured this
  // catches it. Preserve the attempted URL via ?returnUrl= so the user
  // lands on the intended page after re-authentication.
  //
  // Page-refresh race: on a fresh tab/refresh, _token is restored from
  // sessionStorage synchronously but _currentUser is null until the
  // async restoreUser() (GET /auth/me) completes. A user with a valid
  // session who navigates DIRECTLY to a guarded route may briefly hit
  // this branch and be redirected to login. After restoreUser() resolves
  // the user can navigate back. Documented as known UX trade-off; the
  // alternative (making the guard async) is a larger change tracked
  // separately.
  if (!userRole) {
    return router.createUrlTree(['/auth/login'], {
      queryParams: { returnUrl: state.url },
    });
  }

  // Support both single-string and string-array forms of requiredRole.
  // When requiredRole is unset, the role check is a passthrough — but a
  // requiredPermission may still gate the route.
  const roleAllowed = !requiredRole
    ? true
    : Array.isArray(requiredRole)
      ? requiredRole.includes(userRole)
      : userRole === requiredRole;

  // Permission check (defence-in-depth alongside the backend RBAC). The
  // helper returns true for CORPORATE_OWNER regardless of permission so
  // owners always pass.
  const permissionAllowed = requiredPermission
    ? authService.hasPermission(requiredPermission)
    : true;

  const allowed = roleAllowed && permissionAllowed;

  if (!allowed) {
    // Do NOT redirect to /forbidden — redirect to the user's home
    // dashboard. /forbidden is reserved for explicit access-denied flows,
    // not role/permission mismatches (see 2026-04-05 hardening decision
    // in docs/project-decisions.md). Corporate users land on the corporate
    // dashboard so a sub-user who typed /corporate/approvals without the
    // APPROVE_REQUEST permission ends up back where they started.
    //
    // Note: no observability hook here. CLAUDE.md prohibits console.* in
    // production; if a future SIEM/analytics service is added, this is
    // the place to emit a "guard rejected" signal (route + role, no PII).
    const isCorporate =
      userRole === 'CORPORATE_OWNER' || userRole === 'CORPORATE_USER';
    return router.createUrlTree([isCorporate ? '/corporate/dashboard' : '/dashboard']);
  }

  return true;
};
